import React, { useEffect, useRef, useState } from "react";

const CHAT_API_URL = "https://dev.justgen.org:3000/fundraiser/chat";

export default function FloatingChatWidget() {
    const [sessionId] = useState(() => String(Date.now()));
    const [message, setMessage] = useState("");
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    
    // State to toggle the chat window
    const [isOpen, setIsOpen] = useState(false);
    
    const messagesEndRef = useRef(null);

    // Auto-scroll to bottom when new messages arrive
    useEffect(() => {
        if (isOpen) {
            messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }
    }, [messages, loading, isOpen]);

    const sendMessage = async (e) => {
        e.preventDefault();
        const userMessage = message.trim();
        if (!userMessage || loading) return;

        setMessage("");
        setError("");
        setMessages((prev) => [...prev, { role: "user", text: userMessage }]);
        setLoading(true);

        try {
            const response = await fetch(CHAT_API_URL, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ sessionId, message: userMessage }),
            });

            if (!response.ok) {
                throw new Error(`Request failed with status ${response.status}`);
            }

            const data = await response.json();

            setMessages((prev) => [
                ...prev,
                {
                    role: "assistant",
                    text: data.reply || "Sorry, I couldn't generate a response.",
                    sources: data.sources || [],
                    answeredFromKnowledge: data.answeredFromKnowledge ?? false,
                },
            ]);
        } catch (err) {
            setError("Unable to connect to the chatbot. Please try again.");
            console.error("Chat API error:", err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ position: 'fixed', bottom: '24px', right: '24px', zIndex: 9999 }}>
            
            {/* ================= CHAT WINDOW ================= */}
            {isOpen && (
                <div 
                    className="card shadow-lg border-0 rounded-4 overflow-hidden mb-3"
                    style={{ 
                        width: '360px', 
                        height: '550px', 
                        display: 'flex', 
                        flexDirection: 'column',
                        animation: 'slideUp 0.3s ease-out'
                    }}
                >
                    {/* Header */}
                    <div className="p-3 text-white d-flex justify-content-between align-items-center" style={{ background: '#1e293b' }}>
                        <div className="d-flex align-items-center gap-2">
                            <div className="d-flex align-items-center justify-content-center rounded-circle" style={{ width: 32, height: 32, background: 'rgba(251,191,36,0.2)', color: '#fbbf24' }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                                </svg>
                            </div>
                            <div>
                                <h6 className="mb-0 fw-bold" style={{ fontSize: '0.95rem' }}>JGF Assistant</h6>
                                <small style={{ fontSize: '0.7rem', color: '#94a3b8' }}>Online</small>
                            </div>
                        </div>
                        <button 
                            onClick={() => setIsOpen(false)}
                            className="btn btn-sm text-white p-0 border-0"
                            style={{ opacity: 0.7 }}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {/* Messages Area */}
                    <div className="flex-grow-1 p-3" style={{ overflowY: 'auto', background: '#f8fafc' }}>
                        
                        {messages.length === 0 && (
                            <div className="text-center text-muted py-5">
                                <div className="mb-3 d-inline-flex align-items-center justify-content-center rounded-circle" style={{ width: 60, height: 60, background: 'rgba(251,191,36,0.15)', color: '#d97706' }}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                                    </svg>
                                </div>
                                <h6 className="fw-bold" style={{ color: '#1e293b' }}>Welcome!</h6>
                                <p className="small">Ask me anything about the Just Generation Foundation.</p>
                            </div>
                        )}

                        {messages.map((item, index) => (
                            <div key={index} className={`d-flex mb-3 ${item.role === "user" ? "justify-content-end" : "justify-content-start"}`}>
                                <div
                                    className={`p-3 ${item.role === "user" ? "rounded-4 rounded-bottom-right-0" : "rounded-4 rounded-bottom-left-0"}`}
                                    style={{
                                        maxWidth: "85%",
                                        background: item.role === "user" ? '#1e293b' : '#ffffff',
                                        color: item.role === "user" ? '#ffffff' : '#1e293b',
                                        border: item.role === "user" ? 'none' : '1px solid #e2e8f0',
                                        fontSize: '0.9rem',
                                        boxShadow: '0 2px 5px rgba(0,0,0,0.02)'
                                    }}
                                >
                                    <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.5 }}>{item.text}</div>
                                </div>
                            </div>
                        ))}

                        {loading && (
                            <div className="d-flex justify-content-start mb-3">
                                <div className="p-3 rounded-4 rounded-bottom-left-0 bg-white border" style={{ fontSize: '0.9rem' }}>
                                    <span className="spinner-border spinner-border-sm me-2 text-warning" role="status" aria-hidden="true" />
                                    Thinking...
                                </div>
                            </div>
                        )}

                        {error && <div className="alert alert-danger py-2 small rounded-3">{error}</div>}

                        <div ref={messagesEndRef} />
                    </div>

                    {/* Input Area */}
                    <div className="p-3 bg-white border-top">
                        <form onSubmit={sendMessage} className="d-flex gap-2">
                            <input
                                type="text"
                                className="form-control rounded-pill bg-light border-0 px-3"
                                placeholder="Type your message..."
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                disabled={loading}
                                style={{ fontSize: '0.9rem' }}
                            />
                            <button
                                type="submit"
                                className="btn rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                                disabled={loading || !message.trim()}
                                style={{ width: 40, height: 40, background: '#fbbf24', color: '#1e293b' }}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                                </svg>
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {/* ================= FLOATING TOGGLE BUTTON ================= */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="btn rounded-circle shadow-lg d-flex align-items-center justify-content-center border-0"
                style={{
                    width: '60px',
                    height: '60px',
                    background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
                    color: '#1f2937',
                    float: 'right',
                    transition: 'transform 0.2s ease',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.05)')}
                onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
            >
                {isOpen ? (
                    // Close Icon
                    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                ) : (
                    // Chat Icon
                    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                    </svg>
                )}
            </button>

            {/* Inline styles for the slide-up animation */}
            <style>
                {`
                    @keyframes slideUp {
                        from { opacity: 0; transform: translateY(20px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                    .rounded-bottom-right-0 { border-bottom-right-radius: 4px !important; }
                    .rounded-bottom-left-0 { border-bottom-left-radius: 4px !important; }
                `}
            </style>
        </div>
    );
}